import React from "react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { PDFType, PDFContext } from "./types";
import { pdfFactory } from "./PDFFactory";

// Sanitize filename to remove dangerous characters and path traversal
const sanitizeFilename = (filename: string): string => {
  if (!filename) return "";
  // Remove path traversal attempts, special characters, and control characters
  return filename
    .replace(/[\/\\<>:"|?*\x00-\x1f]/g, "") // Remove path separators, invalid filename chars
    .replace(/\.\./g, "") // Remove path traversal
    .trim()
    .slice(0, 255); // Limit length to prevent issues
};

// Utility function to generate PDF filename
export const generatePDFFilename = (
  type: PDFType,
  customName?: string,
  extension: string = "pdf",
): string => {
  const config = pdfFactory.getPDFConfig(type);
  let baseName: string;

  if (customName) {
    // Sanitize custom name
    baseName = sanitizeFilename(customName);
    // Ensure it has the extension
    if (!baseName.toLowerCase().endsWith(`.${extension}`)) {
      baseName = `${baseName}.${extension}`;
    }
  } else {
    baseName = config?.fileName || `${type}.${extension}`;
    // Add timestamp if no custom name provided
    const timestamp = new Date().toISOString().split("T")[0];
    const nameWithoutExt = baseName.replace(`.${extension}`, "");
    return `${nameWithoutExt}_${timestamp}.${extension}`;
  }

  return baseName;
};

// Utility function to create PDF context
export const createPDFContext = (
  type: PDFType,
  data: any,
  customConfig?: Partial<PDFContext["config"]>,
  metadata?: PDFContext["metadata"],
  cmsData?: PDFContext["cmsData"],
  activityIconConverted?: string,
): PDFContext => {
  const defaultConfig = pdfFactory.getPDFConfig(type);

  // Sanitize custom config filename if provided
  const sanitizedCustomConfig = customConfig?.fileName
    ? { ...customConfig, fileName: sanitizeFilename(customConfig.fileName) }
    : customConfig;

  return {
    config: {
      title: defaultConfig?.title,
      fileName: defaultConfig?.fileName || "document.pdf",
      pageSize: defaultConfig?.pageSize,
      orientation: defaultConfig?.orientation,
      ...sanitizedCustomConfig,
    },
    data,
    cmsData,
    metadata: {
      generatedAt: new Date(),
      generatedBy: "Hats and Ladders",
      version: "1.0",
      ...metadata,
    },
    activityIconConverted,
  };
};

// Utility function to validate PDF data
export const validatePDFData = (type: PDFType, data: any): boolean => {
  return pdfFactory.validatePDFData(type, data);
};

// Utility function to create PDF download link
export const createPDFDownloadLink = (
  type: PDFType,
  data: any,
  customConfig?: Partial<PDFContext["config"]>,
  metadata?: PDFContext["metadata"],
  cmsData?: PDFContext["cmsData"],
  activityIconConverted?: string,
): {
  component: React.ReactElement;
  filename: string;
  context: PDFContext;
} | null => {
  const validationResult = validatePDFData(type, data);

  if (!validationResult) {
    return null;
  }

  const context = createPDFContext(
    type,
    data,
    customConfig,
    metadata,
    cmsData,
    activityIconConverted,
  );
  const pdfComponent = pdfFactory.createPDF(type, context);

  if (!pdfComponent) {
    return null;
  }

  const filename = generatePDFFilename(type, customConfig?.fileName, "pdf");

  return {
    component: pdfComponent,
    filename,
    context,
  };
};

// Hook for PDF generation
export const usePDFGeneration = () => {
  const generatePDF = (
    type: PDFType,
    data: any,
    customConfig?: Partial<PDFContext["config"]>,
    metadata?: PDFContext["metadata"],
    cmsData?: PDFContext["cmsData"],
    activityIconConverted?: string,
  ) => {
    return createPDFDownloadLink(
      type,
      data,
      customConfig,
      metadata,
      cmsData,
      activityIconConverted,
    );
  };

  const isPDFTypeSupported = (type: PDFType): boolean => {
    return pdfFactory.getPDFFactory(type) !== null;
  };

  const getSupportedPDFTypes = (): PDFType[] => {
    return pdfFactory.getAvailablePDFTypes();
  };

  return {
    generatePDF,
    isPDFTypeSupported,
    getSupportedPDFTypes,
    validatePDFData,
  };
};

// Utility function to format data for career plan PDF
export const formatCareerPlanData = (careerPlanData: any): any => {
  // This function can be used to transform raw career plan data
  // into the format expected by the CareerPlanPDF component
  return {
    user: careerPlanData.user || {},
    pathway: careerPlanData.pathway || {},
    hat: careerPlanData.hat || {},
    topHat: careerPlanData.topHat || {},
    skills: careerPlanData.skills || [],
    buildingBlocks: careerPlanData.buildingBlocks || [],
    buildingBlocksActivities: careerPlanData.buildingBlocksActivities || [],
    educationOptions: careerPlanData.educationOptions || [],
    recommendedCourses: careerPlanData.recommendedCourses || [],
    nextSteps: careerPlanData.nextSteps || [],
    coreType: careerPlanData.coreType || {},
    workValues: careerPlanData.workValues || {},
  };
};

// Utility function to format data for certificate PDF
export const formatCertificateData = (certificateData: any): any => {
  return {
    award: certificateData.award || {},
    estimatedCertTime: certificateData.estimatedCertTime || null,
    skills: certificateData.skills || [],
    badge: certificateData.badge || {},
    completionDate: certificateData.completionDate || new Date(),
    currentUser: certificateData.currentUser || {},
    isProskillsAward: certificateData.isProskillsAward || false,
    isIBMAward: certificateData.isIBMAward || false,
    isGenericWorkforceAward: certificateData.isGenericWorkforceAward || false,
  };
};
